import { inject, Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import {
  SetSelectedNodesAction,
  SnackbarErrorAction,
  SnackbarInfoAction,
} from '@alfresco/aca-shared/store';
import { NodeChildAssociation, NodeEntry, SharedLink } from '@alfresco/js-api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  mergeMap,
  switchMap,
  map,
  concatMap,
  tap,
} from 'rxjs/operators';
import { of, zip } from 'rxjs';

import {
  CANCEL_SESSION,
  CancelSessionOfficeAction,
  END_EDITING,
  END_SESSION,
  EndEditingOfficeAction,
  EndSessionOfficeAction,
  START_SESSION,
  StartSessionOfficeAction,
  RESUME_SESSION,
  ResumeSessionOfficeAction,
} from '../actions/extension.actions';
import {
  ALFRESCO_LOCK_OWNER,
  ERROR_STARTING_SESSION,
  MOO_SESSION_ID,
  MOO_SESSION_OWNER,
  MOO_WEB_URL,
  NODE_ID_UNAVAILABLE,
} from '../../constants';
import { MsOnlineEditingService } from '../../services/microsoft-online-editing.service';
import { MsalService } from '../../services/msal.service';
import { WINDOW_TOKEN } from '../../../components/shared/window.token';
import { EndSessionDialogComponent } from '../../components/end-session-dialog/end-session-dialog.component';
import { MicrosoftSessionDetails } from '../../types';
import { DocumentListService } from '@alfresco/adf-content-services';

@Injectable()
export class MicrosoftOnlineEffects {
  private readonly documentListService = inject(DocumentListService);
  private static readonly NOTIFICATION_START_SESSION = {
    SUCCESS: 'MICROSOFT-ONLINE.START_SESSION_SUCCESS',
  };
  private static readonly NOTIFICATION_CANCEL_SESSION = {
    SUCCESS: 'MICROSOFT-ONLINE.CANCEL_SESSION_SUCCESS',
    ERROR: 'MICROSOFT-ONLINE.CANCEL_SESSION_DENIED',
  };
  private static readonly NOTIFICATION_END_SESSION = {
    SUCCESS: 'MICROSOFT-ONLINE.END_SESSION_SUCCESS',
    ERROR: 'MICROSOFT-ONLINE.END_SESSION_DENIED',
  };

  private windowRef: Window;

  constructor(
    private readonly actions$: Actions,
    private readonly msal: MsalService,
    private readonly msOnlineEditingService: MsOnlineEditingService,
    @Inject(WINDOW_TOKEN) private readonly _window: Window,
    private readonly matDialog: MatDialog,
    private readonly router: Router
  ) {}

  startSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType<StartSessionOfficeAction>(START_SESSION),
      mergeMap((action) => zip(of(action), this.msal.fetchAccessToken())),
      mergeMap(([action, token]) => {
        const nodeId = this.getNodeId(action?.payload?.entry);
        if (nodeId) {
          return this.msOnlineEditingService.startEditingSession(nodeId, token);
        }
        throw new Error(NODE_ID_UNAVAILABLE);
      }),
      mergeMap((response: MicrosoftSessionDetails) => {
        if (response?.[MOO_WEB_URL]) {
          this.windowRef = this._window.open(response[MOO_WEB_URL]);
        }
        if (response?.[MOO_SESSION_ID]) {
          return this.msOnlineEditingService.getNodeInfo(
            response[MOO_SESSION_ID]
          );
        }
        throw new Error(ERROR_STARTING_SESSION);
      }),
      concatMap((response: NodeEntry) => {
        this.documentListService.reload();
        return [
          new SnackbarInfoAction(
            MicrosoftOnlineEffects.NOTIFICATION_START_SESSION.SUCCESS
          ),
          new SetSelectedNodesAction([response]),
        ];
      })
    )
  );

  resumeSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ResumeSessionOfficeAction>(RESUME_SESSION),
      map((action) => {
        if (action?.payload?.entry?.properties?.[MOO_WEB_URL]) {
          this.windowRef = this._window.open(
            action.payload.entry.properties[MOO_WEB_URL]
          );
          return;
        }
        throw new Error(ERROR_STARTING_SESSION);
      }),
      switchMap(() => [
        new SnackbarInfoAction(
          MicrosoftOnlineEffects.NOTIFICATION_START_SESSION.SUCCESS
        ),
      ])
    )
  );

  cancelSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CancelSessionOfficeAction>(CANCEL_SESSION),
      mergeMap((action) => zip(of(action), this.msal.fetchAccessToken())),
      tap(() => {
        if (this.windowRef) {
          this.windowRef.close();
        }
      }),
      mergeMap(([action, token]) => {
        const nodeId = this.getNodeId(action?.payload?.entry);
        if (nodeId && action.payload.entry.properties?.[MOO_WEB_URL]) {
          return this.msOnlineEditingService
            .cancelEditingSession(nodeId, token)
            .pipe(
              switchMap(() => {
                return this.msOnlineEditingService.getNodeInfo(nodeId);
              }),
              switchMap((response: NodeEntry) => {
                this.documentListService.reload();
                return [
                  new SnackbarInfoAction(
                    MicrosoftOnlineEffects.NOTIFICATION_CANCEL_SESSION.SUCCESS
                  ),
                  new SetSelectedNodesAction([response]),
                ];
              }),
              catchError(() =>
                of(
                  new SnackbarErrorAction(
                    MicrosoftOnlineEffects.NOTIFICATION_CANCEL_SESSION.ERROR
                  )
                )
              )
            );
        }
        throw new Error(NODE_ID_UNAVAILABLE);
      })
    )
  );

  endEditing$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<EndEditingOfficeAction>(END_EDITING),
        mergeMap((action) => {
          const node = action?.payload;
          return this.matDialog
            .open(EndSessionDialogComponent, {
              data: { node },
              width: '570px',
              restoreFocus: true,
            })
            .afterClosed();
        })
      ),
    { dispatch: false }
  );

  endSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType<EndSessionOfficeAction>(END_SESSION),
      mergeMap((action) => zip(of(action), this.msal.fetchAccessToken())),
      tap(() => {
        if (this.windowRef) {
          this.windowRef.close();
        }
      }),
      mergeMap(([action, token]) => {
        const nodeId = this.getNodeId(action?.payload?.node?.entry);
        const sessionNodeId =
          action?.payload?.node?.entry?.properties?.[MOO_SESSION_ID];
        const sessionOwner =
          action?.payload?.node?.entry?.properties?.[MOO_SESSION_OWNER];
        const lockOwner =
          action?.payload?.node?.entry?.properties?.[ALFRESCO_LOCK_OWNER]?.id;

        if (nodeId && sessionNodeId === nodeId && sessionOwner === lockOwner) {
          return this.msOnlineEditingService
            .endEditingSession(
              nodeId,
              token,
              action.payload.isMajor,
              action.payload.comment
            )
            .pipe(
              switchMap(() => {
                return this.msOnlineEditingService.getNodeInfo(nodeId);
              }),
              switchMap((response: NodeEntry) => {
                if (this.router.url.includes('/preview/')) {
                  this._window.location.reload();
                }

                return [
                  new SnackbarInfoAction(
                    MicrosoftOnlineEffects.NOTIFICATION_END_SESSION.SUCCESS
                  ),
                  new SetSelectedNodesAction([response]),
                ];
              }),
              catchError(() =>
                of(
                  new SnackbarErrorAction(
                    MicrosoftOnlineEffects.NOTIFICATION_END_SESSION.ERROR
                  )
                )
              )
            );
        }
        throw new Error(NODE_ID_UNAVAILABLE);
      })
    )
  );

  private getNodeId(entry: NodeChildAssociation): string {
    if (entry instanceof SharedLink) {
      const sharedLink = entry as SharedLink;
      return sharedLink.nodeId;
    } else if (entry?.isFile) {
      return entry.id;
    }
    return null;
  }
}
